import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { TcsmStates } from '../../../../root/tcsm-model';
import { NewHireEnrollmentStep } from './models/new-hire-enrollment-models';
import { BenefitEnrollment } from './enrollment/benefit-enrollment';
import { NewHireStepper } from '../new-hire/components/new-hire-stepper';
import { TcsmAppConfigs } from '../../../../global/models/configuration-models';
import EmploymentRecord from './employment/employment-record';
import { useNewHireSettings } from '../new-hire/hooks/useNewHireSettings';
import { NewHireEnrollmentRoutes } from './models/new-hire-routes';
import { NewHireEnrollmentConfirmation } from './confirmation/new-hire-confirmation';
import WhoWillEnroll from './who-will-enroll/who-will-enroll';
import { withEmployeeSelect } from '../../../../global/components/HOCs/withEmployeeSelect';
import { unSetEmploymentState } from '../employment/employment-slice';
import { unloadCompensationState } from '../compensation/compensation-history/compensation-history-slice';
import { ClearTcsmSessionStorage } from '../../../../global/helpers/session-storage-helper';
import { unloadPersonState } from '../../../account-info/features/personal-info/personal-info-slice';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { unloadRelationshipState } from '../../../account-info/features/relationships/relationships-slice';
import { unloadDependentState } from '../../../account-info/features/dependents/dependents-slice';
import { clearNewHireEnrollmentState } from './slice/new-hire-enrollment-slices';
import { clearPensionInfo } from '../pension-enrollment/pension-enrollment-slice';
import { ArcRoutes } from '../../constants/routes';
import { NewHireConfirmation } from '../new-hire/components/new-hire-confirmation';
import { withGroupAdminRights } from '../../../../Global/components/HOCs/withGroupAdminRights';
import { useCurrentEmployerHook } from './hooks/useCurrentEmployerId';
import { clearEmploymentCompensation } from '../employment-compensation/employment-compensation-slices';
import { useQuery } from '../../../../global/hooks/useQuery';
import { FormTypes } from '../../../account-info/models/form-models';
import { useNewHireEnrollmentAllowed } from './hooks/useNewhireEnrollmentAllowed';
import { isDioAdmin, isInstitutionOfficer } from '../../../../global/helpers/auth-helpers';
import { HoursIncreaseEventType, NewHireEventType } from '../../constants/constant-strings';
import { ConfirmationStatusCode } from './models/new-hire-confirmation-models';
import { WhoWillEnrollContentModel } from './models/who-will-enroll-models';
import { replaceAll } from '../../../../global/helpers/string-helpers';
import { NewHireProps } from '../../models/arc-epi-props';


const WhoWillEnrollWrapper = withGroupAdminRights()(withEmployeeSelect()(WhoWillEnroll));
const BenefitEnrollmentWrapper = withGroupAdminRights()(withEmployeeSelect()(BenefitEnrollment));


interface Props {
	appSettings: TcsmAppConfigs;
	contentModelHook:()=> WhoWillEnrollContentModel;
	eventType?: string;
	confirmationCode?: ConfirmationStatusCode;
}

function NewHireEnrollmentApp(props: Props) {

	const { appSettings, contentModelHook, eventType, confirmationCode } = props;
	const settingsLoaded = useNewHireSettings(appSettings?.newHireSettingsId);
	const routeMatch = useRouteMatch();
	const dispatch = useDispatch();
	const currentStep = useSelector<TcsmStates, NewHireEnrollmentStep>(state => state?.newHireEnrollmentState?.currentStep);
	const location = useLocation();
	const newHireEnrollmentAllowed = useNewHireEnrollmentAllowed();
	const roleAllowed = isDioAdmin() || isInstitutionOfficer();
	const formType = useQuery()?.get('success');
	const isStip = formType === FormTypes.STIP_NEW_HIRE;
	const scheduledHourStartDate = useSelector<TcsmStates, string>(state => state?.employmentState?.currentEmployment?.scheduled_hours[0]?.start_date);
	const settings = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);

	useEffect(() => {
		return function cleanUp() {
			dispatch(unSetEmploymentState());
			dispatch(unloadCompensationState());
			dispatch(unloadPersonState(DemographicAppKeys.arc));
			dispatch(unloadRelationshipState(DemographicAppKeys.arc));
			dispatch(unloadDependentState(DemographicAppKeys.arc));
			dispatch(clearNewHireEnrollmentState());
			dispatch(clearPensionInfo());
			dispatch(clearEmploymentCompensation());
			ClearTcsmSessionStorage(false);
		};
	}, []);

	if (!settingsLoaded) {
		return <LoadingCircle />;
	}

	function isStipConfirmationPageWithoutWhoWillEnroll() {
		return location?.pathname?.includes(ArcRoutes.NewHireConfirmation) && !(isStip && newHireEnrollmentAllowed && roleAllowed);
	}

	return (
		<>
			<NewHireStepper
				hideSteps={!currentStep}
				basePath={routeMatch.path}
				hideAll={location?.pathname?.includes(NewHireEnrollmentRoutes.Confirmation) || isStipConfirmationPageWithoutWhoWillEnroll()}
				titleOverwrite={eventType === HoursIncreaseEventType ? settings.scheduledHourEnrollmentTitle : null}
			/>
			<Switch>
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.WhoWillEnroll}`}
					render={
						() =>
							<WhoWillEnrollWrapper
								basePath={routeMatch.path}
								contentModelHook={contentModelHook}
								clientIdHook={useCurrentEmployerHook}
							/>
					}
				/>
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.BenefitEnrollment}`} render={() => <BenefitEnrollmentWrapper settings={appSettings} eventType={eventType} confirmationCode={confirmationCode} qualifyingEventDateOverwrite={eventType === HoursIncreaseEventType ? replaceAll(scheduledHourStartDate, '-', '/') : null} />} />
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.EmployeeRecord}`} render={() =>
					<EmploymentRecord
						appSettings={appSettings}
						successRedirectOverwrite={`${routeMatch.path}${ArcRoutes.NewHireConfirmation}`}
					/>}
				/>
				<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Confirmation}`} render={() => <NewHireEnrollmentConfirmation settignsId={appSettings?.newHireSettingsId} hideBreadCrumbs />} />
				<Route exact path={`${routeMatch.path}${ArcRoutes.NewHireConfirmation}`}
					render={() => (
						<NewHireConfirmation settingsId={appSettings.newHireSettingsId} />
					)}
				/>
				<Route exact path={`${routeMatch.path}/*`} render={() => <Redirect to={`${routeMatch.path}${NewHireEnrollmentRoutes.EmployeeRecord}${ArcRoutes.StipNewHire}`} />} />
			</Switch>
		</>
	);
}

export default NewHireEnrollmentApp;

NewHireEnrollmentApp.defaultProps = {
	eventType: NewHireEventType,
	confirmationCode: null,
};